<template>
  <el-dialog title="上传发票" :visible.sync="visible" width="760px" :close-on-click-modal="false" :close-on-press-escape="false" :before-close="requestClose" append-to-body>
    <div v-if="invoice" class="invoice-summary">
      <div>抬头：{{ invoice.title || '-' }}</div><div>申请编号：{{ invoiceId }}</div>
      <div>开票金额：{{ invoice.totalPrice == null ? '-' : invoice.totalPrice }}</div>
    </div>
    <p class="upload-tip">支持多张图片和多个 PDF，最多 {{ limits.maxFiles }} 个文件，单个不超过 {{ formatSize(limits.maxFileBytes) }}。确认完成后不可更换。</p>
    <el-alert v-if="error" :title="error" type="error" :closable="false" show-icon />
    <div v-loading="loading" class="file-selection">
      <input ref="picker" class="file-picker" type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.gif" :disabled="busy || confirmPending || loading" @change="selectFiles">
      <el-button size="small" :disabled="busy || confirmPending || loading" @click="$refs.picker.click()">选择发票文件</el-button>
      <div v-if="!files.length" class="empty-files">请添加已开具的发票文件</div>
      <div v-for="(file, index) in files" :key="file.clientUploadId" class="selected-file">
        <el-image v-if="file.isImage" class="thumbnail" :src="file.preview" :preview-src-list="[file.preview]" fit="contain" />
        <span v-else class="pdf-icon">PDF</span>
        <div class="file-label"><div>{{ index + 1 }}. {{ file.raw.name }}</div><small>{{ formatSize(file.raw.size) }} · {{ statusLabel(file.status) }}</small><div v-if="file.error" class="file-error">{{ file.error }}</div></div>
        <el-button type="text" size="mini" @click="previewFile(file)">预览</el-button>
        <el-button type="text" size="mini" :disabled="busy || confirmPending" @click="removeFile(index)">移除</el-button>
      </div>
    </div>
    <p v-if="confirmPending" class="upload-tip">提交结果尚未确认，文件清单已保留。请重试确认，系统会核对同一次提交。</p>
    <span slot="footer"><el-button :disabled="busy" @click="requestClose">取消</el-button><el-button type="primary" :loading="busy" :disabled="loading || !files.length" @click="submit">{{ confirmPending ? '重试确认' : '确认上传并完成开票' }}</el-button></span>
  </el-dialog>
</template>

<script>
import api from '@/api/finance/invoiceApplicationAudit'
import { invoiceLimits, operationId, invoiceIdOf, replyData, displaySize, validateSelection, sameCompletedFiles } from './manualInvoice'

export default {
  data() {
    return { visible: false, loading: false, busy: false, error: '', invoice: null, invoiceId: '', batchId: '', requestId: '', files: [], limits: { ...invoiceLimits }, confirmPending: false, epoch: 0 }
  },
  beforeDestroy() { this.epoch++; this.releaseDraft() },
  methods: {
    formatSize: displaySize,
    statusLabel(status) { return { ready: '待上传', uploading: '正在上传', uploaded: '已上传', error: '上传失败，确认时重试' }[status] },
    async handleCreate(row) {
      if (this.visible || this.busy) return
      let id
      try { id = invoiceIdOf(row.invoiceId == null ? row.id : row.invoiceId) } catch (e) { this.$message.error(e.message); return }
      const epoch = ++this.epoch
      this.invoice = { ...row }; this.invoiceId = id; this.batchId = operationId(); this.requestId = operationId()
      this.files = []; this.error = ''; this.confirmPending = false; this.visible = true; this.loading = true
      try {
        const data = replyData(await api.attachments(id))
        if (epoch !== this.epoch) return
        if (Number(data.state) !== 1) throw new Error('该申请已处理，请刷新列表后重试')
        this.limits = { ...invoiceLimits, ...data.limits }
      } catch (e) {
        if (epoch === this.epoch) { this.error = e.message || '暂时无法上传，请重新打开后重试'; this.loading = true }
        return
      }
      if (epoch === this.epoch) this.loading = false
    },
    selectFiles(event) {
      const incoming = Array.from(event.target.files || [])
      event.target.value = ''
      if (this.busy || this.confirmPending || this.loading) return
      try {
        validateSelection(this.files, incoming, this.limits)
        incoming.forEach(raw => this.files.push({ raw, clientUploadId: operationId(), preview: URL.createObjectURL(raw), isImage: /\.(jpe?g|png|gif)$/i.test(raw.name), status: 'ready', attachmentId: '', error: '' }))
        this.error = ''
      } catch (e) { this.error = e.message }
    },
    previewFile(file) {
      const win = window.open(file.preview, '_blank')
      if (win) win.opener = null
      else this.$message.warning('请允许浏览器打开预览窗口后重试')
    },
    async removeFile(index) {
      if (this.busy || this.confirmPending) return
      const file = this.files[index]
      if (!file) return
      const epoch = this.epoch
      this.busy = true
      try {
        replyData(await this.discard([file]))
        if (epoch !== this.epoch) return
        this.files.splice(index, 1); URL.revokeObjectURL(file.preview)
      } catch (e) { if (epoch === this.epoch) this.error = e.message || '文件移除失败，请重试' }
      finally { if (epoch === this.epoch) this.busy = false }
    },
    discard(files, scope = { invoiceId: this.invoiceId, batchId: this.batchId }) {
      return api.deleteStaged({ ...scope, attachmentIds: files.filter(file => file.attachmentId).map(file => file.attachmentId), clientUploadIds: files.map(file => file.clientUploadId) })
    },
    releaseDraft(completed = false) {
      if (!completed && this.files.length) this.discard(this.files).catch(() => {})
      this.files.forEach(file => URL.revokeObjectURL(file.preview)); this.files = []
    },
    requestClose(done) {
      if (this.busy) return
      this.epoch++; this.releaseDraft(); this.visible = false; this.loading = false
      if (typeof done === 'function') done()
    },
    finish() {
      this.confirmPending = false; this.releaseDraft(true); this.visible = false
      this.$message.success('发票已上传，申请已更新为已开票'); this.$emit('completed')
    },
    async submit() {
      if (this.busy || this.loading || !this.files.length) return
      this.busy = true; this.error = ''
      const epoch = this.epoch
      const scope = { invoiceId: this.invoiceId, batchId: this.batchId }
      const files = this.files.slice()
      try {
        for (const file of files) {
          if (file.attachmentId) continue
          const data = new FormData()
          data.append('invoiceId', this.invoiceId); data.append('batchId', this.batchId); data.append('clientUploadId', file.clientUploadId); data.append('file', file.raw)
          file.status = 'uploading'; file.error = ''
          try {
            const uploaded = replyData(await api.uploadAttachment(data))
            if (!uploaded.attachmentId) throw new Error('未获得文件上传结果，请重试')
            file.attachmentId = uploaded.attachmentId; file.status = 'uploaded'
            if (epoch !== this.epoch) { this.discard([file], scope).catch(() => {}); return }
          } catch (e) { file.status = 'error'; file.error = e.message || '上传失败'; throw e }
        }
        this.confirmPending = true
        const ids = files.map(file => file.attachmentId)
        const result = replyData(await api.completeManual({ invoiceId: this.invoiceId, batchId: this.batchId, requestId: this.requestId, attachmentIds: ids }))
        if (epoch !== this.epoch) return
        if (!sameCompletedFiles(result, ids)) throw new Error('未获得一致的开票完成结果')
        this.finish()
      } catch (e) {
        if (epoch !== this.epoch) return
        if (e.businessFailure) { this.confirmPending = false; this.error = e.message; return }
        if (this.confirmPending) {
          try {
            const latest = replyData(await api.attachments(this.invoiceId))
            if (epoch !== this.epoch) return
            if (sameCompletedFiles(latest, this.files.map(file => file.attachmentId))) { this.finish(); return }
          } catch (_) { /* Retain the exact request and files while its result is unknown. */ }
        }
        this.error = this.confirmPending ? '提交结果尚未确认，请重试确认。' : (e.message || '上传未完成，请重试。')
      } finally { if (epoch === this.epoch) this.busy = false }
    }
  }
}
</script>

<style scoped>
.invoice-summary { display: flex; flex-wrap: wrap; gap: 10px 24px; line-height: 24px; }
.upload-tip { color: #606266; font-size: 13px; line-height: 22px; }
.file-selection { min-height: 110px; margin-top: 16px; }
.file-picker { display: none; }
.empty-files { padding: 28px; text-align: center; color: #909399; }
.selected-file { display: flex; align-items: center; gap: 12px; padding: 14px 0; border-bottom: 1px solid #ebeef5; }
.thumbnail, .pdf-icon { width: 44px; height: 48px; flex-shrink: 0; }
.pdf-icon { display: flex; align-items: center; justify-content: center; color: #d33; background: #fff1f0; border-radius: 4px; }
.file-label { flex: 1; min-width: 0; overflow-wrap: anywhere; line-height: 22px; }
.file-label small { color: #909399; }
.file-error { color: #f56c6c; font-size: 12px; }
</style>
